import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { EditableColumnDef, EditableTable } from "../../../../../../shared/components/table/inline-edit-table";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { ITcProductRawMaterialInformation as ImportedITcProductRawMaterialInformation } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";

interface ITcProductRawMaterialInformation extends ImportedITcProductRawMaterialInformation {
  tcProductNo?: string;
  additionalProp1?: any;
}
 
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";


interface RawMaterialsInfoProps {
  rawMaterialInfoData: ITcProductRawMaterialInformation[]; // Expecting an array
  setRawMaterialInfoData: React.Dispatch<
    React.SetStateAction<ITcProductRawMaterialInformation[]>
  >;
}

const RawMaterialDetails: React.FC<RawMaterialsInfoProps> = ({
  rawMaterialInfoData,
  setRawMaterialInfoData,
}) => {
  // CRITICAL FIX: Normalize function to handle all possible field name variations
  const normalizeItem = (item: any): ITcProductRawMaterialInformation => {    
    const normalized = {
      tcProductNo: item.tcProductNo || "",
      tcProductRawMaterialOrder: item.tcProductRawMaterialOrder || "",
      tcProductRawMaterialCode: item.tcProductRawMaterialCode || "",
      tcProductRawMaterialPercentage: item.tcProductRawMaterialPercentage?.toString() || "",
      tcProductRawMaterialUserSpecificTerm: item.tcProductRawMaterialUserSpecificTerm || "",
      additionalProp1: item.additionalProp1 || {}
    };    
    return normalized;
  };

  const [data, setData] = useState<ITcProductRawMaterialInformation[]>(() => {
    const normalized = rawMaterialInfoData.map(normalizeItem);
    return normalized;
  });
  const [tableData, setTableData] = useState<ITcProductRawMaterialInformation[]>(() => {
    return rawMaterialInfoData.map(normalizeItem);
  });
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const { updateField } = useFormActions("input-tc-product-details");
  const [rawMaterials, setRawMaterials] = useState([]);
  const { notify } = useNotification()
  const { isRestrictPopUpSave, setRestrictPopUpSave } = useTransactionCertificateStore();
  const { executeGet } = useApi();
  
  // Add missing variables
  const hasFetchedRef = React.useRef(false);
  const isInternalUpdate = React.useRef(false); // Track internal vs external updates
  const selectedInputTcId = ""; // This should come from props or context
  const selectedProductIds: string[] = []; // This should come from props or context
  const validationSchema = Yup.object().shape({
    tcProductRawMaterialCode: Yup.string().nullable(),
    tcProductRawMaterialPercentage: Yup.number()
      .typeError("Percentage must be a number")
      .positive("Percentage must be positive")
      .max(100, "Percentage cannot exceed 100")
      .test(
        'decimal-places',
        'Percentage can have maximum 2 decimal places',
        function (value: any) {
          if (!value) return true;
          const decimalPart = value.toString().split('.')[1];
          return !decimalPart || decimalPart.length <= 2;
        }
      )
      .test(
        'percentage-equals-100',
        'percentage-equals-100',
        function (value: any) {
          if (editingRowIndex > -1) {
            let total = data.reduce((sum, item, index) => {
              if (index === editingRowIndex) return sum;
              return sum + parseFloat(item.tcProductRawMaterialPercentage || "0");
            }, 0);
            if ((total + value) > 100) {
              notify("Raw Material Percentage cannot be greater than 100", "error");
              return false;
            }
          }
          return true;

        }
      )
      .nullable(),
    tcProductRawMaterialUserSpecificTerm: Yup.string().nullable(),
  }).test(
    'at-least-one',
    'At least one field is required',
    function (values) {
      return values.tcProductRawMaterialCode || values.tcProductRawMaterialPercentage
        ? true
        : this.createError({ message: 'At least one field is required' });
    }
  );
 
  // Fetch raw material codes dropdown options
  useEffect(() => {
    const getRawMaterialCodes = async () => {
      try {
        const result: any = await dtrackItService.getRawMaterialCodes();
        const rawMaterials = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`
        }));
        setRawMaterials(rawMaterials);
      } catch (err) {
        Utills.cle(`Error in Fetching Organic Farm Standard Code ${err}`)
      }
    };
    getRawMaterialCodes();
  }, []);
 
  // Initialize data from props only once or when props change externally
  useEffect(() => {
    // Skip if this is an internal update
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    if (rawMaterialInfoData && rawMaterialInfoData.length > 0) {
      const normalized = rawMaterialInfoData.map(normalizeItem);
      setData(normalized);
      setTableData(normalized);
    }
  }, [rawMaterialInfoData]);
 
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [selectedInputTcId, selectedProductIds]);
 
  // Helper function to update parent without causing re-render loop
  const updateParentData = (updatedData: ITcProductRawMaterialInformation[]) => {
    const normalizedData = updatedData.map((item, index) => normalizeItem({
      ...item,
      tcProductRawMaterialOrder: (index + 1).toString()
    }));
    
    // Mark as internal update to prevent useEffect loop
    isInternalUpdate.current = true;
    
    updateField("raw-materials-info", normalizedData);
    setRawMaterialInfoData(normalizedData);
  };

  const formik = useFormik<ITcProductRawMaterialInformation>({
    initialValues: {
      tcProductRawMaterialOrder: "",
      tcProductRawMaterialCode: "",
      tcProductRawMaterialPercentage: "",
      tcProductRawMaterialUserSpecificTerm: "",
      additionalProp1: {}
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

    const handleAddNewRow = () => {
    const newRow: ITcProductRawMaterialInformation = {
      tcProductRawMaterialOrder: "",
      tcProductRawMaterialCode: "",
      tcProductRawMaterialPercentage: "",
      tcProductRawMaterialUserSpecificTerm: "",
    };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setRestrictPopUpSave(true)
    formik.setValues(newRow);
    formik.setTouched({});
  };
 
  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    
    // Update formik immediately
    formik.setFieldValue(columnId, value);
    
    // Update data state
    setData((prevData) =>
      prevData.map((row, index) => {
        if (index === rowIndex) {
          const updated = {
            ...row,
            [columnId]: value,
            tcProductRawMaterialOrder: (index + 1).toString()
          };
          return updated;
        }
        return row;
      })
    );
  };

  const handleCancelEdit = async () => {
    if (editingRowIndex !== -1) {
      setData(tableData);
    }
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
  };

  const handleSaveRow = () => {
    if (editingRowIndex === -1 || !formik.isValid) return;
    
    if (!formik.values.tcProductRawMaterialCode || !formik.values.tcProductRawMaterialPercentage) {
      notify("Raw Material Code and Percentage are required", "error");
      return;
    } 
    // Create updated row with ALL fields explicitly
    const updatedRow: ITcProductRawMaterialInformation = {
      tcProductNo: formik.values.tcProductNo || "",
      tcProductRawMaterialOrder: (editingRowIndex + 1).toString(),
      tcProductRawMaterialCode: formik.values.tcProductRawMaterialCode || "",
      tcProductRawMaterialPercentage: formik.values.tcProductRawMaterialPercentage || "",
      tcProductRawMaterialUserSpecificTerm: formik.values.tcProductRawMaterialUserSpecificTerm || "",
      additionalProp1: formik.values.additionalProp1 || {}
    };

    // Update data array - preserve ALL fields for all rows
    const newData = data.map((row, index) => {
      if (index === editingRowIndex) {
        return updatedRow;
      }
      // Preserve all fields for non-edited rows
      return normalizeItem({
        ...row,
        tcProductRawMaterialOrder: (index + 1).toString()
      });
    });
  
    setData(newData);
    setTableData(newData);
    updateParentData(newData); // Use helper to prevent loop
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
    notify("Row saved successfully", "success");
  };

 
  const columns: EditableColumnDef<ITcProductRawMaterialInformation>[] = [
    {
      accessorKey: "tcProductRawMaterialCode",
      header: "Raw Material Code",
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          label=""
          name="tcProductRawMaterialCode"
          value={value || ''}
          options={rawMaterials}
          enableSearch={true}
          onChange={(e) => onChange(e.target.value)}
        />
      ),
    },
    {
      accessorKey: "tcProductRawMaterialPercentage",
      header: "Raw Material Percentage",
      renderEditCell: ({ value, onChange }) => (
       
        <CustomTextField
          name="tcProductRawMaterialPercentage"
          value={value || ''}
          inputProps={{ max: 100, step: "0.01" }}
          type="number"
          onChange={(e) => {
            const newValue = e.target.value;
            // Allow empty, numbers with up to 2 decimal places, and values <= 100
            if (!newValue || (Number(newValue) <= 100 && /^\d*\.?\d{0,2}$/.test(newValue))) {
              onChange(newValue);
            }
          }}
          placeholder="Enter value"
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "tcProductRawMaterialUserSpecificTerm",
      header: "User Specific Term",
      Cell: ({ row }) => {
        const value = row.original.tcProductRawMaterialUserSpecificTerm;
        return <span>{value || '-'}</span>;
      },
      renderEditCell: ({ value, onChange }) => {
        return (
          <CustomTextField
            name="tcProductRawMaterialUserSpecificTerm"
            value={value || ''}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            placeholder="Enter user specific term"
            size="small"
          />
        );
      },
    },
  ];
 
  const handleEditRow = (rowIndex: number) => {
    
    const row = data[rowIndex];    
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    
    formik.setValues({
      tcProductNo: row.tcProductNo || "",
      tcProductRawMaterialOrder: row.tcProductRawMaterialOrder || "",
      tcProductRawMaterialCode: row.tcProductRawMaterialCode || "",
      tcProductRawMaterialPercentage: row.tcProductRawMaterialPercentage || "",
      tcProductRawMaterialUserSpecificTerm: row.tcProductRawMaterialUserSpecificTerm || "",
      additionalProp1: row.additionalProp1 || {}
    });
    
  };
 
  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data
      .filter((_, index) => index !== rowIndex)
      .map((row, index) => normalizeItem({
        ...row,
        tcProductRawMaterialOrder: (index + 1).toString()
      }));
    
    setData(updatedData);
    setTableData(updatedData);
    updateParentData(updatedData); // Use helper to prevent loop
  };
 
  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton disabled={isRestrictPopUpSave} onClick={handleAddNewRow} sx={{ mb: 2 }}>
            Add New Row
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
        />
      </Box>
    </div>
  );
};

export default RawMaterialDetails;



